import React, { useEffect, useState } from 'react';
import ModalShell from './ModalShell';
import Input from './Input';
import DropdownSelect from './DropdownSelect';
import Textarea from './Textarea';
import Button from './Button';
import TagPicker from './TagPicker';
import ColorHexInput from './ColorHexInput';
import SingleImagePicker from './SingleImagePicker';
import type { UploadProgress } from '../lib/upload/retry';
import SectionHelp from './SectionHelp';
import OverflowMenu, { type OverflowMenuItem } from './OverflowMenu';
import { useDirtyTracker } from '../hooks/useDirtyTracker';
import { sectionBoxP6 } from '../lib/modalClasses';
import { Icon } from './Icon';
import {
  MATERIAL_SECTIONS,
  sectionFields,
  visibleSections,
  type MaterialFieldSpec,
  type MaterialSectionSpec,
} from '../lib/masterMaterialFields';

export interface MaterialFormManufacturer {
  id: number;
  name: string;
  slug: string;
  is_owned?: boolean;
}

export interface MaterialFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** Translator (tenant `useLocale().t`; admin Spanish-fallback resolver). */
  t: (key: string) => string;
  title: string;
  mode: 'create' | 'edit';

  /** The entity field-spec (e.g. FILAMENT_FIELDS) + sections (defaults to all). */
  fields: readonly MaterialFieldSpec[];
  sections?: readonly MaterialSectionSpec[];

  manufacturers: MaterialFormManufacturer[];
  /** Closed list for the `material_type` select (FFF whitelist for filament). */
  materialTypeOptions: readonly string[];
  /** Named option sets for any OTHER `select` field, keyed by its `optionsKey`
   *  (e.g. supply 'kinds', manufacturer 'countries'). */
  optionSets?: Record<string, readonly { value: string; label: string }[]>;
  availableTags: readonly string[];
  availableCertifications?: readonly string[];

  /** String-valued form state for text/number/select/colors/textarea fields,
   *  keyed by the field `key`. The 'colors' field stores the comma-separated
   *  hex list under its key (`primary_color_hex`). Owned by the wrapper. */
  values: Record<string, string>;
  onValueChange: (key: string, value: string) => void;

  tags: string[];
  onTagsChange: (tags: string[]) => void;
  certifications: string[];
  onCertificationsChange: (certs: string[]) => void;

  /** Image — staged like any other field: `onPickImage` holds the picked File
   *  and `onClearImage` marks the current image for removal; the wrapper applies
   *  both on Save. `imageRemoved` feeds the dirty signal so a pending removal
   *  enables Save and arms the unsaved-changes guard. (`onUploadImage` is the
   *  legacy immediate-upload path, kept for any non-staged caller.) */
  pendingImage?: File | null;
  onPickImage?: (file: File | null) => void;
  existingImageUrl?: string | null;
  onUploadImage?: (file: File) => Promise<void>;
  onClearImage?: () => Promise<void> | void;
  imageRemoved?: boolean;
  /** Progress of `pendingImage` while the wrapper's Save uploads it. */
  imageUpload?: UploadProgress;

  /** Auto-derived slug (manufacturer.slug + material_name), shown read-only. */
  slug?: string;

  valid: boolean;
  submitting: boolean;
  onSubmit: () => void | Promise<void>;
  /** Edit-mode 3-dot menu rendered to the right of the Save button (e.g.
   *  Clonar + Activar/Desactivar). Only shown in edit mode and when non-empty. */
  headerMenu?: OverflowMenuItem[];
  /** Optional read-only info rendered under the slug (e.g. uuid in edit mode). */
  footer?: React.ReactNode;
  /** Optional error message shown in a red box at the top of the form. */
  error?: string | null;
  /** Hide the Basic/Advanced toggle and render ALL fields — for simple entities
   *  (supply/manufacturer) that have no advanced tier. */
  hideTierToggle?: boolean;
  /** Read-only view (e.g. tenant viewing a global, admin-owned row): every
   *  control is disabled and the save/delete/status actions are hidden. */
  readOnly?: boolean;
  /** Optional banner rendered at the top of the modal body, OUTSIDE the disabled
   *  fieldset so its own actions stay clickable. Wrappers pass it only in
   *  read-only mode (e.g. the "Global / Solo lectura" support notice). */
  readOnlyNotice?: React.ReactNode;

  /** Optional node rendered in the header, in the SAME spot as the (master) Save
   *  button. Used by the tenant spec sheet — where the master Save is hidden
   *  (readOnly) — to place its own "Guardar nota" button. The two never coexist
   *  (admin shows the master Save and passes no slot). */
  headerActionSlot?: React.ReactNode;
  /** Optional section rendered BELOW all field sections and OUTSIDE the disabled
   *  fieldset, so it stays writable in an otherwise read-only form (e.g. the
   *  tenant's per-material customer notes). Always shown — ignores the
   *  Basic/Advanced tier. */
  belowSectionsSlot?: React.ReactNode;
  /** Extra dirty signal OR-ed into the unsaved-changes guard AND the Save
   *  button's enabled state, so a pending edit outside the master form state
   *  (e.g. `extrasSlot` file attachments) both arms the "leave without saving"
   *  prompt and lets the user actually Save it. */
  extraDirty?: boolean;
  /** Optional node rendered INSIDE the "Extras" (notes) section box, below its
   *  fields. Used by the supply form to host a <FileAttachmentZone> in Extras.
   *  Lives inside the disabled fieldset (read-only forms disable it too). */
  extrasSlot?: React.ReactNode;
}

const FULL_WIDTH = new Set(['colors', 'tags', 'certifications', 'image', 'textarea']);

// Static class maps so Tailwind's purge sees every literal.
const GRID_COLS: Record<number, string> = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' };
const SPAN_CLASS: Record<number, string> = { 1: '', 2: 'sm:col-span-2', 3: 'sm:col-span-3' };

/** Column span of a field within a `cols`-wide grid. Full-width controls span
 *  the whole row; otherwise honour `field.span` (default 1), capped at `cols`. */
function fieldSpan(field: MaterialFieldSpec, cols: number): number {
  if (FULL_WIDTH.has(field.control)) return cols;
  return Math.min(field.span ?? 1, cols);
}

export default function MaterialForm({
  isOpen,
  onClose,
  t,
  title,
  mode,
  fields,
  sections = MATERIAL_SECTIONS,
  manufacturers,
  materialTypeOptions,
  optionSets = {},
  availableTags,
  availableCertifications = [],
  values,
  onValueChange,
  tags,
  onTagsChange,
  certifications,
  onCertificationsChange,
  pendingImage = null,
  onPickImage,
  imageUpload,
  existingImageUrl,
  onUploadImage,
  onClearImage,
  imageRemoved,
  slug,
  valid,
  submitting,
  onSubmit,
  headerMenu,
  footer,
  error,
  hideTierToggle,
  readOnly,
  readOnlyNotice,
  headerActionSlot,
  belowSectionsSlot,
  extraDirty,
  extrasSlot,
}: MaterialFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const effectiveAdvanced = hideTierToggle ? true : showAdvanced;
  const hasSlugField = fields.some((f) => f.control === 'slug');

  // Suppress the dirty signal (and the unsaved-changes guard) until the user
  // actually interacts. Wrappers prefill `values` in an effect AFTER open, so a
  // baseline captured at mount would read as dirty the instant the prefill
  // lands — opening a row and closing it without typing would wrongly prompt
  // "leave without saving". `touched` resets on every open/close; the first
  // real edit flips it, freezing the baseline at the prefilled value.
  const [touched, setTouched] = useState(false);
  useEffect(() => { setTouched(false); }, [isOpen]);

  // The active/inactive toggle applies immediately (its own API call), so it is
  // deliberately NOT part of the Save dirty signature.
  const { isDirty } = useDirtyTracker(
    {
      ...values,
      __tags: tags.join(','),
      __certs: certifications.join(','),
      __img: pendingImage?.name ?? '',
      __imgRemoved: imageRemoved ? '1' : '',
    },
    { pinned: !touched },
  );

  // Edit handlers that also mark the form touched. The image is now staged
  // (pick/remove update pending state and apply on Save), so both feed the
  // dirty signal like any other field.
  const valueChange = (key: string, value: string) => { setTouched(true); onValueChange(key, value); };
  const tagsChange = (next: string[]) => { setTouched(true); onTagsChange(next); };
  const certsChange = (next: string[]) => { setTouched(true); onCertificationsChange(next); };
  const pickImage = onPickImage ? (file: File | null) => { setTouched(true); onPickImage(file); } : undefined;
  const clearImage = onClearImage ? () => { setTouched(true); return onClearImage(); } : undefined;

  const sectionByKey = new Map(sections.map((s) => [s.key, s]));
  const visible = visibleSections(fields, effectiveAdvanced);

  const renderControl = (field: MaterialFieldSpec, opts?: { fillImage?: boolean }): React.ReactNode => {
    const label = t(field.labelKey);
    const val = values[field.key] ?? '';
    // Read-only rows (a global entry) have nothing to fill in.
    const markRequired = Boolean(field.required) && !readOnly;

    switch (field.control) {
      case 'select': {
        const options =
          field.optionsKey === 'manufacturers'
            ? [
                { value: '', label: '—' },
                ...manufacturers.map((m) => ({
                  value: String(m.id),
                  label: m.is_owned ? `${m.name} (${t('catalog.ownerPrivate')})` : m.name,
                })),
              ]
            : field.optionsKey === 'materialTypes'
            ? [{ value: '', label: '—' }, ...materialTypeOptions.map((o) => ({ value: o, label: o }))]
            : (optionSets[field.optionsKey ?? ''] ?? []).map((o) => ({ value: o.value, label: o.label }));
        return (
          <DropdownSelect
            label={label}
            markRequired={markRequired}
            value={val}
            onChange={(v) => valueChange(field.key, v)}
            options={options}
            searchable
            surface="transparent"
            dropdownWidth="w-full"
            placeholder="—"
          />
        );
      }
      case 'number':
        return (
          <Input
            label={field.unit ? `${label} (${field.unit})` : label}
            markRequired={markRequired}
            type="number"
            value={val}
            min={field.min}
            max={field.max}
            step={field.step}
            placeholder={field.placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => valueChange(field.key, e.target.value)}
          />
        );
      case 'textarea':
        return (
          <Textarea
            label={label}
            markRequired={markRequired}
            value={val}
            rows={3}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => valueChange(field.key, e.target.value)}
          />
        );
      case 'tags':
        return (
          <TagPicker
            label={label}
            value={tags}
            onChange={tagsChange}
            availableTags={availableTags}
            addTagLabel={t('catalog.addTag')}
          />
        );
      case 'certifications':
        return (
          <TagPicker
            label={label}
            value={certifications}
            onChange={certsChange}
            availableTags={availableCertifications}
            addTagLabel={t('catalog.addTag')}
          />
        );
      case 'image':
        return (
          <SingleImagePicker
            t={t}
            label={label}
            readOnly={readOnly}
            fill={opts?.fillImage}
            pendingFile={pendingImage}
            onPick={pickImage}
            imageUrl={existingImageUrl}
            onUpload={onUploadImage}
            onClear={clearImage}
            upload={imageUpload}
          />
        );
      case 'slug':
        return (
          <Input
            label={label}
            markRequired={markRequired}
            value={val}
            maxLength={field.maxLength ?? 120}
            placeholder={slug || field.placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              valueChange(field.key, e.target.value.toLowerCase())
            }
          />
        );
      case 'colors':
        return (
          <ColorHexInput
            label={label}
            value={val}
            onChange={(v) => valueChange(field.key, v)}
            disabled={readOnly}
            warning={t('catalog.colorsApproxWarning')}
            hint={t('catalog.colorsHexHint')}
            addLabel={t('catalog.addColor')}
            removeLabel={t('catalog.removeColor')}
            applyLabel={t('catalog.applyColor')}
          />
        );
      case 'url': {
        const raw = (val ?? '').trim();
        const href = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
        const isLink = /^https?:\/\//i.test(href);
        // Read-only (e.g. a global row): render the URL as a truncated, underlined
        // link that opens in a new tab — `<a>` isn't disabled by the fieldset.
        if (readOnly) {
          return (
            <div className="pt-2 min-w-0">
              <span className="block text-[10px] font-bold text-primary uppercase tracking-wider truncate">{label}</span>
              {isLink ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block truncate text-sm font-medium text-primary underline underline-offset-2 hover:text-primary-dark"
                >
                  {raw}
                </a>
              ) : (
                <span className="mt-1 block text-sm text-slate-400 dark:text-slate-500">—</span>
              )}
            </div>
          );
        }
        // Editable: the input, plus an underlined "open link" shortcut once the
        // value is a valid URL (the suffix slot is pointer-events-none, so this
        // lives below the field instead).
        return (
          <div className="min-w-0">
            <Input
              label={label}
              markRequired={markRequired}
              type="url"
              value={val}
              maxLength={field.maxLength}
              placeholder={field.placeholder}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => valueChange(field.key, e.target.value)}
            />
            {isLink && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary underline underline-offset-2 hover:text-primary-dark"
              >
                <Icon name="open_in_new" className="!text-[14px]" />
                {t('catalog.openUrl')}
              </a>
            )}
          </div>
        );
      }
      case 'text':
      default:
        return (
          <Input
            label={label}
            markRequired={markRequired}
            value={val}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => valueChange(field.key, e.target.value)}
          />
        );
    }
  };

  const advancedToggle = (
    <div
      role="group"
      aria-label={t('catalog.viewLabel')}
      className="inline-flex rounded-lg border border-border-subtle dark:border-border-subtle-dark p-0.5 bg-slate-50 dark:bg-slate-800"
    >
      {([
        ['basic', t('catalog.viewBasic'), false],
        ['advanced', t('catalog.viewAdvanced'), true],
      ] as const).map(([key, lbl, adv]) => (
        <button
          key={key}
          type="button"
          aria-pressed={adv === showAdvanced}
          onClick={() => setShowAdvanced(adv)}
          className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${
            adv === showAdvanced
              ? 'bg-white dark:bg-surface-dark text-primary shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          {lbl}
        </button>
      ))}
    </div>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      dirty={isDirty || !!extraDirty}
      title={title}
      maxWidth="max-w-3xl"
      size="auto"
      headerActions={
        <div className="flex items-center gap-2">
          {!hideTierToggle && advancedToggle}
          {!readOnly && (
            <Button variant="primary" size="md" requires="write" onClick={() => onSubmit()} disabled={!valid || submitting || !(isDirty || !!extraDirty)}>
              {submitting ? t('common.saving') : mode === 'create' ? t('common.create') : t('common.save')}
            </Button>
          )}
          {/* Tenant-only: the per-material "Guardar nota" button takes the master
              Save's (hidden, readOnly) slot. Independent action — saves only the
              note, never the read-only master. */}
          {headerActionSlot}
          {/* Shown even when readOnly so a tenant can clone a global (read-only)
              row into a private copy; the menu items themselves are non-mutating
              or gate their own permissions. */}
          {mode === 'edit' && headerMenu && headerMenu.length > 0 && (
            <OverflowMenu ariaLabel={t('common.actions')} items={headerMenu} />
          )}
        </div>
      }
    >
      {/* Read-only notice (e.g. global-row support banner) sits ABOVE the form so
          it stays outside the disabled fieldset and its own buttons stay live. */}
      {readOnlyNotice}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <fieldset disabled={readOnly} className="space-y-4 border-0 p-0 m-0 min-w-0">
        {error && (
          <div className="px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        {visible.map((sectionKey) => {
          const section = sectionByKey.get(sectionKey);
          if (!section) return null;
          const sFields = sectionFields(fields, sectionKey, effectiveAdvanced);
          if (sFields.length === 0) return null;
          const cols = section.columns ?? 2;
          // Identity renders a bespoke layout when it owns the image: the image
          // + any `slot:'aside'` fields stack on the left, the rest in a 2-col
          // grid on the right.
          const imageField = sectionKey === 'identity' ? sFields.find((f) => f.control === 'image') : undefined;
          const asideFields = imageField ? sFields.filter((f) => f.slot === 'aside') : [];
          const fullFields = imageField ? sFields.filter((f) => f.slot === 'full') : [];
          const gridFields = imageField
            ? sFields.filter((f) => f.control !== 'image' && f.slot !== 'aside' && f.slot !== 'full')
            : sFields;
          // Square image side: a fixed-width square (CSS can't derive a square's
          // side from a sibling's height in a flex row — width is sized before
          // the stretched height — so we size it to ~the grid's height via a
          // row-count heuristic). A labelled Input row is ~48px (label + h-10)
          // and grid gaps are 16px, so N rows ≈ N*48 + (N-1)*16: 2 rows ≈ 112px
          // (w-28), 3 rows ≈ 176px (w-44). Keeps the image from overflowing past
          // the last field row.
          const gridRows = Math.ceil(
            gridFields.reduce((n, f) => n + fieldSpan(f, 2), 0) / 2,
          );
          const imageColW = gridRows <= 2 ? 'sm:w-28' : 'sm:w-44';
          return (
            <div key={sectionKey} className={sectionBoxP6}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Icon name={section.icon} className="!text-[16px]" aria-hidden />
                  {t(section.labelKey)}
                </div>
                <SectionHelp t={t} fields={sFields} />
              </div>
              {imageField ? (
                <>
                  {/* Image (left) — a fixed-width SQUARE sized (via `imageColW`)
                      to roughly the height of the right grid's rows, so it's
                      never taller than the fields beside it. On mobile it's a
                      full-width square on top. */}
                  <div className="flex flex-col sm:flex-row gap-4 sm:items-stretch">
                    <div className={`w-full ${imageColW} shrink-0`}>
                      {renderControl(imageField, { fillImage: true })}
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
                      {gridFields.map((field) => (
                        <div key={field.key} className={SPAN_CLASS[fieldSpan(field, 2)]}>
                          {renderControl(field)}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* GTIN + the two SKUs as a full-width 3-column row below. */}
                  {asideFields.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                      {asideFields.map((field) => (
                        <div key={field.key}>{renderControl(field)}</div>
                      ))}
                    </div>
                  )}
                  {/* Full-width rows (e.g. the long product URL) below everything. */}
                  {fullFields.length > 0 && (
                    <div className="space-y-4 mt-4">
                      {fullFields.map((field) => (
                        <div key={field.key}>{renderControl(field)}</div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className={`grid grid-cols-1 ${GRID_COLS[cols]} gap-4`}>
                  {gridFields.map((field) => (
                    <div key={field.key} className={SPAN_CLASS[fieldSpan(field, cols)]}>
                      {renderControl(field)}
                    </div>
                  ))}
                </div>
              )}
              {sectionKey === 'notes' && extrasSlot && (
                <div className="mt-4">{extrasSlot}</div>
              )}
              {sectionKey === 'notes' && ((slug && !hasSlugField) || footer) && (
                <div className="mt-3 text-[11px] text-slate-400 dark:text-slate-500 font-mono break-all">
                  {slug && !hasSlugField && <div>slug: {slug}</div>}
                  {footer}
                </div>
              )}
            </div>
          );
        })}
        </fieldset>
      </form>
      {/* Writable extras (e.g. tenant customer notes) — OUTSIDE the disabled
          fieldset and below every section, so they stay editable even when the
          form (the global master) is read-only. `mt-4` matches the inter-section
          gap (the fieldset's space-y-4) so it doesn't glue to the last section. */}
      {belowSectionsSlot && <div className="mt-4">{belowSectionsSlot}</div>}
    </ModalShell>
  );
}
